import React, { useState, useRef } from 'react';
import { API_URL } from '../config';

/**
 * PhotoUploader component with drag-and-drop, reordering, and deletion.
 *
 * Features:
 * - Drag and drop file upload
 * - File browser button
 * - Shows thumbnails of uploaded photos
 * - Drag to reorder photos
 * - Delete button on each photo
 * - Upload progress indicator
 * - Error messages for failed uploads
 *
 * @param {number} hikeId - Hike ID (null for new hikes not yet created)
 * @param {Object[]} photos - Current photos array
 * @param {Function} onPhotosChange - Callback when photos change
 * @param {number} maxPhotos - Maximum photos allowed (default: 10)
 * @param {string} token - Auth token for uploads
 */
function PhotoUploader({ hikeId, photos = [], onPhotosChange, maxPhotos = 10, token }) {
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [error, setError] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const fileInputRef = useRef(null);

    // If hike doesn't exist yet, we can't upload photos
    const canUpload = hikeId !== null && hikeId !== undefined;

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleFileInput = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    const handleFiles = async (files) => {
        if (!canUpload) {
            setError('Please save the hike first before uploading photos');
            return;
        }

        // Filter for image files
        const imageFiles = files.filter(file =>
            file.type.startsWith('image/')
        );

        if (imageFiles.length === 0) {
            setError('Please select valid image files');
            return;
        }

        // Check max photos limit
        if (photos.length + imageFiles.length > maxPhotos) {
            setError(`Maximum ${maxPhotos} photos allowed. You can upload ${maxPhotos - photos.length} more.`);
            return;
        }

        setError(null);
        setUploading(true);

        try {
            // Build multipart form data
            const formData = new FormData();
            imageFiles.forEach(file => {
                formData.append('files', file);
            });

            // Upload to API
            setUploadProgress(`Uploading ${imageFiles.length} photo(s)...`);

            const response = await fetch(`${API_URL}/hikes/${hikeId}/media`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const detail = errorData.detail;
                throw new Error(typeof detail === 'string' ? detail : 'Upload failed');
            }

            const data = await response.json();

            // Add new photos to the list
            onPhotosChange([...photos, ...data.media]);

            setUploadProgress(null);
        } catch (err) {
            setError(err.message);
            setUploadProgress(null);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (photoId) => {
        if (!canUpload) return;

        if (!window.confirm('Are you sure you want to delete this photo?')) {
            return;
        }

        try {
            const response = await fetch(`${API_URL}/hikes/${hikeId}/media/${photoId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const detail = errorData.detail;
                throw new Error(typeof detail === 'string' ? detail : 'Delete failed');
            }

            // Remove photo from list
            onPhotosChange(photos.filter(p => p.id !== photoId));
        } catch (err) {
            setError(err.message);
        }
    };

    // Drag to reorder
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOverReorder = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        // Reorder photos
        const newPhotos = [...photos];
        const draggedPhoto = newPhotos[draggedIndex];
        newPhotos.splice(draggedIndex, 1);
        newPhotos.splice(index, 0, draggedPhoto);

        setDraggedIndex(index);
        onPhotosChange(newPhotos);
    };

    const handleDragEndReorder = async () => {
        if (!canUpload || draggedIndex === null) {
            setDraggedIndex(null);
            return;
        }

        // Save new order to backend
        try {
            const mediaIds = photos.map(p => p.id);

            const response = await fetch(`${API_URL}/hikes/${hikeId}/media/reorder`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ media_ids: mediaIds })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const detail = errorData.detail;
                throw new Error(typeof detail === 'string' ? detail : 'Reorder failed');
            }

            const data = await response.json();
            onPhotosChange(data.media);
        } catch (err) {
            setError(err.message);
        } finally {
            setDraggedIndex(null);
        }
    };

    return (
        <div className="space-y-4">
            {/* Upload zone */}
            {canUpload && photos.length < maxPhotos && (
                <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        dragOver
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                    >
                        <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600">
                        Drag and drop photos here, or{' '}
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                            browse
                        </button>
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                        {photos.length} / {maxPhotos} photos uploaded
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileInput}
                        className="hidden"
                    />
                </div>
            )}

            {/* Upload progress */}
            {uploading && uploadProgress && (
                <div className="flex items-center justify-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span>{uploadProgress}</span>
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {/* Warning for new hikes */}
            {!canUpload && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
                    <p className="text-sm">
                        <strong>Nota:</strong> Potrai caricare delle foto dopo aver creato la gita.
                    </p>
                </div>
            )}

            {/* Photo thumbnails */}
            {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {photos.map((photo, index) => (
                        <div
                            key={photo.id}
                            draggable={canUpload}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOverReorder(e, index)}
                            onDragEnd={handleDragEndReorder}
                            className="relative group cursor-move"
                        >
                            <img
                                src={photo.thumbnail_url || photo.media_url}
                                alt={`Thumbnail ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg"
                            />
                            {/* Delete button */}
                            {canUpload && (
                                <button
                                    type="button"
                                    onClick={() => handleDelete(photo.id)}
                                    className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                                    title="Delete photo"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            )}
                            {/* Order indicator */}
                            <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                                {index + 1}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default PhotoUploader;
